/**
 * 障害インシデント `/incidents`（A157 / A140 / A167）。
 *
 * 一覧 + 選択した 1 件の詳細パネル（起票・状態更新・公開文面・個人データ漏えい報告）。
 *
 * **この画面がいちばん効くのは漏えい報告の期限管理**。メールホスティングでは
 * 1 件の侵害が「不正の目的による漏えいのおそれ」に直ちに該当し、**件数を問わず**
 * 個人情報保護委員会への報告義務が生じる（個人情報保護法 26 条・規則 7 条）。
 * 速報の期限は覚知から 3 日で、**延ばせない**。API は `ppcReportDueAt` と
 * `reportOverdue` を返しているので、赤帯で必ず見せる。
 *
 * 公開文面（`publicMessage`）は障害告知の本文。ここを空のままにすると
 * 「ステータスページに出す文面が無い」ままインシデントが閉じる。
 */
import * as React from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Megaphone, Plus, Send, Siren } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  Input,
  KeyValue,
  PageHeader,
  Section,
  StatusBadge,
  SwitchRow,
  Textarea,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import {
  INCIDENT_SEVERITY_LABEL,
  INCIDENT_SEVERITY_TONE,
  INCIDENT_STATUS_LABEL,
  INCIDENT_STATUS_TONE,
  labelFrom,
  optionsFromLabels,
  toneFrom,
} from '../lib/ops-labels.js';
import { usePermission } from '../stores/auth.js';
import { FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { DetailSkeleton, ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { IncidentDetail, IncidentSummary } from '../types/api.js';

const routeApi = getRouteApi(RID.incidents);

export function IncidentsPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canWrite = usePermission(OPS_PERMISSION.securityWrite);
  const [createOpen, setCreateOpen] = React.useState(false);

  const list = useCursorList<IncidentSummary>({
    path: '/incidents',
    keyFor: (params) => qk.incidents(params),
    filters: {},
    refetchInterval: 60_000,
  });

  const select = (id: string): void => {
    // 一覧の下に詳細を出すだけ（同じ画面）。main を先頭へ戻すと選んだ行と詳細が画面外へ消える。
    void navigate({ search: (prev) => ({ ...prev, incidentId: id }), resetScroll: false });
  };

  const columns: Array<DataTableColumn<IncidentSummary>> = [
    {
      id: 'reference',
      header: '番号',
      width: 'w-36',
      cell: (i) => <code className="font-mono text-xs">{i.reference}</code>,
    },
    {
      id: 'severity',
      header: '重大度',
      width: 'w-32',
      cell: (i) => (
        <StatusBadge
          status={i.severity}
          size="sm"
          dot
          tone={toneFrom(INCIDENT_SEVERITY_TONE, i.severity)}
          label={labelFrom(INCIDENT_SEVERITY_LABEL, i.severity)}
        />
      ),
    },
    { id: 'title', header: '件名', cell: (i) => <span className="font-medium">{i.title}</span> },
    {
      id: 'status',
      header: '状態',
      cell: (i) => (
        <StatusBadge
          status={i.status}
          size="sm"
          dot
          pulse={i.status === 'open'}
          tone={toneFrom(INCIDENT_STATUS_TONE, i.status)}
          label={labelFrom(INCIDENT_STATUS_LABEL, i.status)}
        />
      ),
    },
    {
      id: 'public',
      header: '公開文面',
      hideBelow: 'md',
      // 公開文面が無いまま閉じたインシデントは、契約者から見ると「何も説明が無かった障害」。
      cell: (i) =>
        i.publicMessage ? (
          <Badge variant="success" size="sm">
            あり
          </Badge>
        ) : (
          <Badge variant="muted" size="sm">
            未作成
          </Badge>
        ),
    },
    { id: 'startedAt', header: '発生', hideBelow: 'md', cell: (i) => formatDateTime(i.startedAt) },
    {
      id: 'resolvedAt',
      header: '解消',
      hideBelow: 'lg',
      cell: (i) => (i.resolvedAt ? formatRelative(i.resolvedAt) : '—'),
    },
  ];

  return (
    <>
      <PageHeader
        title="障害インシデント"
        description="障害の起票・状態更新・公開文面の作成と、個人データ漏えい報告の期限管理。"
        actions={
          <>
            <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
            {canWrite ? (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus aria-hidden />
                インシデントを起票
              </Button>
            ) : null}
          </>
        }
      />

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(i) => i.id}
        loading={list.query.isLoading}
        onRowClick={(i) => select(i.id)}
        selectedKey={search.incidentId ?? null}
        empty={<ListEmpty filtered={false} entityLabel="インシデント" />}
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      {search.incidentId ? <IncidentPanel incidentId={search.incidentId} canWrite={canWrite} /> : null}

      <CreateIncidentDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={select} />
    </>
  );
}

/* ------------------------------------------------------------- 詳細 */

function IncidentPanel({
  incidentId,
  canWrite,
}: {
  incidentId: string;
  canWrite: boolean;
}): React.ReactElement {
  const query = useQuery({
    queryKey: qk.incident(incidentId),
    queryFn: () => api.get<IncidentDetail>(`/incidents/${incidentId}`),
  });

  if (query.isLoading) {
    return (
      <Section title="インシデントの詳細" className="mt-6">
        <DetailSkeleton />
      </Section>
    );
  }
  if (query.isError) {
    return <QueryError error={query.error} onRetry={() => void query.refetch()} className="mt-6" />;
  }
  const incident = query.data;
  if (!incident) return <></>;

  return (
    <div className="mt-6 space-y-5">
      {incident.personalData.reportOverdue ? (
        <Alert variant="destructive">
          <AlertTitle>個人情報保護委員会への速報が期限を過ぎています</AlertTitle>
          <AlertDescription>
            期限（{formatDateTime(incident.personalData.reportDueAt)}）を過ぎています。
            速報は覚知から 3 日で、<strong>延ばせません</strong>。直ちに報告し、下の「速報を報告済みにする」を押してください。
          </AlertDescription>
        </Alert>
      ) : incident.personalData.involved && incident.personalData.prelimReportedAt === null ? (
        <Alert variant="warning">
          <AlertTitle>
            速報の期限: {formatRelative(incident.personalData.reportDueAt)}（
            {formatDateTime(incident.personalData.reportDueAt)}）
          </AlertTitle>
          <AlertDescription>
            個人データが関係するインシデントです。件数を問わず報告義務があります。
          </AlertDescription>
        </Alert>
      ) : null}

      <Section title={`${incident.reference} ${incident.title}`}>
        <KeyValue
          columns={3}
          items={[
            { key: '重大度', value: labelFrom(INCIDENT_SEVERITY_LABEL, incident.severity) },
            { key: '状態', value: labelFrom(INCIDENT_STATUS_LABEL, incident.status) },
            { key: '発生', value: formatDateTime(incident.startedAt) },
            { key: '覚知', value: formatDateTime(incident.detectedAt) },
            { key: '暫定対応', value: formatDateTime(incident.mitigatedAt) },
            { key: '解消', value: formatDateTime(incident.resolvedAt) },
            { key: '影響ノード', value: `${incident.affectedNodeIds.length} 台` },
            { key: '影響組織', value: `${incident.affectedOrganizationIds.length} 組織` },
            { key: '事後分析', value: incident.postmortemUrl ?? '—' },
          ]}
        />

        {incident.summary ? (
          <div className="mt-4 space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">概要</p>
            <p className="whitespace-pre-wrap text-sm">{incident.summary}</p>
          </div>
        ) : null}

        <div className="mt-4 space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            公開文面（契約者への告知）
          </p>
          {incident.publicMessage ? (
            <p className="whitespace-pre-wrap rounded-md border border-border bg-panel p-3 text-sm">
              {incident.publicMessage}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              まだ作成されていません。告知するには文面が要ります。
            </p>
          )}
        </div>
      </Section>

      <Section title="個人データ漏えいの報告">
        <KeyValue
          columns={3}
          items={[
            { key: '個人データの関与', value: incident.personalData.involved ? 'あり' : 'なし' },
            {
              key: '影響を受けた本人の数',
              value:
                incident.personalData.affectedSubjectCount === null
                  ? '未確定'
                  : `${incident.personalData.affectedSubjectCount} 名`,
            },
            { key: '速報の期限', value: formatDateTime(incident.personalData.reportDueAt) },
            { key: '速報', value: formatDateTime(incident.personalData.prelimReportedAt) },
            { key: '確報', value: formatDateTime(incident.personalData.finalReportedAt) },
            { key: '受付番号', value: incident.personalData.receiptNo ?? '—' },
            {
              key: '本人への通知',
              value: formatDateTime(incident.personalData.dataSubjectNotifiedAt),
            },
          ]}
        />
      </Section>

      {canWrite ? <IncidentActions incident={incident} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------- 更新 */

function IncidentActions({ incident }: { incident: IncidentDetail }): React.ReactElement {
  const [statusOpen, setStatusOpen] = React.useState(false);
  const [publicOpen, setPublicOpen] = React.useState(false);
  const [announceOpen, setAnnounceOpen] = React.useState(false);
  const [privacyOpen, setPrivacyOpen] = React.useState(false);

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => setStatusOpen(true)}>
          <Siren aria-hidden />
          状態・重大度を更新
        </Button>
        <Button variant="outline" size="sm" onClick={() => setPublicOpen(true)}>
          <Megaphone aria-hidden />
          公開文面を編集
        </Button>
        {/*
         * **配信の入口はここ 1 つだけ。** 公開文面を保存しても契約者には
         * 1 文字も届かない（`incidents.public_message` を読む先が
         * `POST /ops/incidents/:id/announce` しか無い）。以前はこの画面が
         * 「配信は告知画面から」と案内し、告知画面（/announcements）は
         * 「文面の作成と告知は /incidents 側」と案内していて、
         * **どちらにもボタンが無いまま互いを指していた**。
         */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAnnounceOpen(true)}
          disabled={!incident.publicMessage}
          title={incident.publicMessage ? undefined : '公開文面が未作成のため告知できません'}
        >
          <Send aria-hidden />
          契約者へ告知する
        </Button>
        <Button variant="outline" size="sm" onClick={() => setPrivacyOpen(true)}>
          漏えい報告を記録
        </Button>
      </div>

      <UpdateStatusDialog incident={incident} open={statusOpen} onOpenChange={setStatusOpen} />
      <PublicMessageDialog incident={incident} open={publicOpen} onOpenChange={setPublicOpen} />
      <AnnounceDialog incident={incident} open={announceOpen} onOpenChange={setAnnounceOpen} />
      <PrivacyDialog incident={incident} open={privacyOpen} onOpenChange={setPrivacyOpen} />
    </>
  );
}

/**
 * 公開文面を契約者へ配る（`POST /ops/incidents/:id/announce`）。
 *
 * サーバー側は `.strict()` で `{broadcast?, email?}` のみ。**文面はここでは
 * 送らない**（保存済みの `public_message` をサーバーが読む）。
 *
 * 冪等キーは `incident-<id>-<status>` なので、**同じ状態のまま 2 回押しても
 * 未読の通知は増えない**。状態を進めてから押せば続報として届く。
 */
function AnnounceDialog({
  incident,
  open,
  onOpenChange,
}: {
  incident: IncidentDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [broadcast, setBroadcast] = React.useState(false);
  // 既定はサーバーと同じ判断（sev1 / sev2 はメールでも送る）。
  const defaultEmail = incident.severity === 'sev1' || incident.severity === 'sev2';
  const [email, setEmail] = React.useState(defaultEmail);
  const [result, setResult] = React.useState<{ notified: number; failed: number } | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setBroadcast(false);
    setEmail(defaultEmail);
    setResult(null);
  }, [open, defaultEmail]);

  const announce = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; notified: number; failed: number; email: boolean }>(
        `/incidents/${incident.id}/announce`,
        { broadcast, email },
      ),
    onSuccess: (res) => {
      setResult({ notified: res.notified, failed: res.failed });
      void qc.invalidateQueries({ queryKey: qk.incident(incident.id) });
      void qc.invalidateQueries({ queryKey: qk.announcements() });
    },
  });

  const affected = incident.affectedOrganizationIds.length;
  const byNode = incident.affectedNodeIds.length;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="公開文面を契約者へ告知する"
      description="保存済みの公開文面をそのまま配ります。ここで文面は変更できません（先に「公開文面を編集」で直してください）。"
      submitLabel={result ? '閉じる' : '告知する'}
      pending={announce.isPending}
      error={announce.error}
      onSubmit={() => {
        if (result) onOpenChange(false);
        else announce.mutate();
      }}
      wide
    >
      {result ? (
        <Alert variant={result.failed > 0 ? 'warning' : 'default'}>
          <AlertTitle>
            {result.notified} 組織へ配信しました{result.failed > 0 ? `（${result.failed} 組織で失敗）` : ''}
          </AlertTitle>
          <AlertDescription>
            {result.failed > 0
              ? '失敗した組織には届いていません。通知の設定を確認してから、状態を進めたうえでもう一度告知してください。'
              : '同じ状態のまま押し直しても、未読の通知は増えません（続報は状態を進めてから配ってください）。'}
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="whitespace-pre-wrap rounded-md border border-border bg-panel p-3 text-sm">
            {incident.publicMessage}
          </div>

          <SwitchRow
            label="全契約者へ配る（全体障害）"
            description={
              broadcast
                ? '有効・停止中を含む全組織に配ります。影響が特定の顧客に限られるなら切ってください。'
                : `影響先として記録された ${affected} 組織${byNode > 0 ? `と、影響ノード ${byNode} 台に載っている組織` : ''}に配ります。`
            }
            checked={broadcast}
            onCheckedChange={setBroadcast}
          />
          <SwitchRow
            label="メールでも送る"
            description={
              defaultEmail
                ? `${labelFrom(INCIDENT_SEVERITY_LABEL, incident.severity)} は既定でメールも送ります。`
                : 'コントロールパネルの通知だけでよければ切ったままにします。'
            }
            checked={email}
            onCheckedChange={setEmail}
          />

          {!broadcast && affected === 0 && byNode === 0 ? (
            <Alert variant="warning">
              <AlertTitle>配信先が 0 組織です</AlertTitle>
              <AlertDescription>
                影響組織も影響ノードも記録されていません。このまま告知しても誰にも届きません。
                「状態・重大度を更新」から影響範囲を記録するか、全契約者へ配ってください。
              </AlertDescription>
            </Alert>
          ) : null}
        </>
      )}
    </FormDialog>
  );
}

/** `PATCH /incidents/:id` を投げて一覧と詳細を両方引き直す。 */
function useIncidentPatch(incidentId: string, onDone: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/incidents/${incidentId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.incident(incidentId) });
      void qc.invalidateQueries({ queryKey: ['ops', 'incidents'] });
      onDone();
    },
  });
}

function UpdateStatusDialog({
  incident,
  open,
  onOpenChange,
}: {
  incident: IncidentDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const [status, setStatus] = React.useState(incident.status);
  const [severity, setSeverity] = React.useState(incident.severity);
  const [summary, setSummary] = React.useState(incident.summary);
  const [postmortemUrl, setPostmortemUrl] = React.useState(incident.postmortemUrl ?? '');

  React.useEffect(() => {
    if (!open) return;
    setStatus(incident.status);
    setSeverity(incident.severity);
    setSummary(incident.summary);
    setPostmortemUrl(incident.postmortemUrl ?? '');
  }, [open, incident]);

  const patch = useIncidentPatch(incident.id, () => onOpenChange(false));

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="インシデントを更新する"
      submitLabel="更新する"
      pending={patch.isPending}
      error={patch.error}
      onSubmit={() =>
        // 差分のあった項目だけ送る。全項目を送ると、他の運営者が同時に直した
        // 値をこちらの古い値で上書きしてしまう。
        patch.mutate({
          ...(status !== incident.status ? { status } : {}),
          ...(severity !== incident.severity ? { severity } : {}),
          ...(summary !== incident.summary ? { summary } : {}),
          ...(postmortemUrl !== (incident.postmortemUrl ?? '')
            ? { postmortemUrl: postmortemUrl.trim().length > 0 ? postmortemUrl.trim() : null }
            : {}),
        })
      }
      wide
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="状態" required>
          <NativeSelect value={status} onChange={setStatus} options={optionsFromLabels(INCIDENT_STATUS_LABEL)} />
        </Field>
        <Field label="重大度" required>
          <NativeSelect
            value={severity}
            onChange={setSeverity}
            options={optionsFromLabels(INCIDENT_SEVERITY_LABEL)}
          />
        </Field>
      </div>

      <Field label="概要（社内向け）">
        <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={5} />
      </Field>

      <Field label="事後分析の URL" hint="空にすると解除します。">
        <Input
          value={postmortemUrl}
          onChange={(e) => setPostmortemUrl(e.target.value)}
          placeholder="https://…"
          spellCheck={false}
        />
      </Field>
    </FormDialog>
  );
}

function PublicMessageDialog({
  incident,
  open,
  onOpenChange,
}: {
  incident: IncidentDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const [text, setText] = React.useState(incident.publicMessage ?? '');

  React.useEffect(() => {
    if (open) setText(incident.publicMessage ?? '');
  }, [open, incident.publicMessage]);

  const patch = useIncidentPatch(incident.id, () => onOpenChange(false));

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="公開文面を編集する"
      description="契約者に見せる告知の本文です。原因の断定は避け、影響範囲と現在の状況を書きます。"
      submitLabel="保存する"
      pending={patch.isPending}
      error={patch.error}
      onSubmit={() => patch.mutate({ publicMessage: text.trim().length > 0 ? text : null })}
      wide
    >
      <Field label="公開文面" hint="2000 文字まで。空にすると未作成に戻します。">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          maxLength={2000}
          placeholder={
            '現在、一部のお客様において受信が遅延する事象を確認しています。原因の調査と復旧作業を進めています。'
          }
        />
      </Field>
      <Alert>
        <AlertDescription>
          保存しただけでは配信されません。閉じたあと「契約者へ告知する」を押すと、影響組織
          （または全契約者）へ通知とメールで配ります。
        </AlertDescription>
      </Alert>
    </FormDialog>
  );
}

function PrivacyDialog({
  incident,
  open,
  onOpenChange,
}: {
  incident: IncidentDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const pd = incident.personalData;
  const [involved, setInvolved] = React.useState(pd.involved);
  const [count, setCount] = React.useState(pd.affectedSubjectCount?.toString() ?? '');
  const [prelim, setPrelim] = React.useState(pd.prelimReportedAt !== null);
  const [final, setFinal] = React.useState(pd.finalReportedAt !== null);
  const [receiptNo, setReceiptNo] = React.useState(pd.receiptNo ?? '');
  const [notified, setNotified] = React.useState(pd.dataSubjectNotifiedAt !== null);

  React.useEffect(() => {
    if (!open) return;
    setInvolved(pd.involved);
    setCount(pd.affectedSubjectCount?.toString() ?? '');
    setPrelim(pd.prelimReportedAt !== null);
    setFinal(pd.finalReportedAt !== null);
    setReceiptNo(pd.receiptNo ?? '');
    setNotified(pd.dataSubjectNotifiedAt !== null);
  }, [open, pd]);

  const patch = useIncidentPatch(incident.id, () => onOpenChange(false));

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="個人データ漏えいの報告を記録する"
      description="「個人データの関与あり」を立てた時点で、覚知から 3 日の速報期限が動き出します。"
      submitLabel="記録する"
      pending={patch.isPending}
      error={patch.error}
      onSubmit={() =>
        patch.mutate({
          personalDataInvolved: involved,
          affectedSubjectCount: count.trim().length > 0 ? Number(count) : null,
          ppcPrelimReported: prelim,
          ppcFinalReported: final,
          ppcReceiptNo: receiptNo.trim().length > 0 ? receiptNo.trim() : null,
          dataSubjectNotified: notified,
        })
      }
      wide
    >
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={involved}
          onChange={(e) => setInvolved(e.target.checked)}
          className="size-4 rounded border-border"
        />
        個人データが関係している
      </label>

      <Field label="影響を受けた本人の数" hint="未確定なら空のままにします。件数が 1 件でも報告義務があります。">
        <Input
          value={count}
          onChange={(e) => setCount(e.target.value.replace(/[^0-9]/g, ''))}
          inputMode="numeric"
        />
      </Field>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={prelim}
            onChange={(e) => setPrelim(e.target.checked)}
            className="size-4 rounded border-border"
          />
          速報を報告済みにする
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={final}
            onChange={(e) => setFinal(e.target.checked)}
            className="size-4 rounded border-border"
          />
          確報を報告済みにする（不正アクセス等は覚知から 60 日）
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={notified}
            onChange={(e) => setNotified(e.target.checked)}
            className="size-4 rounded border-border"
          />
          本人への通知を済ませた
        </label>
      </div>

      <Field label="受付番号" hint="委員会から返る番号。控えを残します。">
        <Input value={receiptNo} onChange={(e) => setReceiptNo(e.target.value)} spellCheck={false} />
      </Field>
    </FormDialog>
  );
}

/* ------------------------------------------------------------- 起票 */

function CreateIncidentDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [title, setTitle] = React.useState('');
  const [severity, setSeverity] = React.useState('sev3');
  const [summary, setSummary] = React.useState('');
  const [publicMessage, setPublicMessage] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setTitle('');
    setSeverity('sev3');
    setSummary('');
    setPublicMessage('');
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/incidents', {
        title: title.trim(),
        severity,
        summary,
        ...(publicMessage.trim().length > 0 ? { publicMessage: publicMessage.trim() } : {}),
      }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['ops', 'incidents'] });
      onOpenChange(false);
      onCreated(created.id);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="インシデントを起票する"
      description="番号（INC-年-連番）は自動で採番されます。覚知時刻は起票した時刻になります。"
      submitLabel="起票する"
      pending={submit.isPending}
      error={submit.error}
      disabled={title.trim().length === 0}
      onSubmit={() => submit.mutate()}
      wide
    >
      <Field label="件名" required>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
      </Field>

      <Field label="重大度" required>
        <NativeSelect
          value={severity}
          onChange={setSeverity}
          options={optionsFromLabels(INCIDENT_SEVERITY_LABEL)}
        />
      </Field>

      <Field label="概要（社内向け）">
        <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} />
      </Field>

      <Field label="公開文面" hint="後から編集できます。空でも起票できます。">
        <Textarea
          value={publicMessage}
          onChange={(e) => setPublicMessage(e.target.value)}
          rows={3}
          maxLength={2000}
        />
      </Field>
    </FormDialog>
  );
}
